import { useState } from 'react';
import { Link } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

const introPoints = [
  '台湾で創業し、健康食品OEM/ODM業界で長年の実績',
  '自社GMP認定工場、国際製造基準に適合',
  '研究開発・生産・品質管理の一貫サービス',
  '600以上のブランドクライアントへのサービス実績',
  '多種類の剤型に対応し、多様な市場ニーズに応える',
];

const brandValues = [
  { icon: 'ri-heart-pulse-line', title: '健康最優先', desc: '消費者の健康を中心に据え、すべての製造工程を厳格に管理します。' },
  { icon: 'ri-lightbulb-line', title: '継続的な革新', desc: 'グローバルな生技トレンドを追い、新原料・新技術を積極的に導入し続けます。' },
  { icon: 'ri-shield-check-line', title: '品質へのコミットメント', desc: 'すべてのロットに完全な検査報告書を発行し、安定した品質をお約束します。' },
  { icon: 'ri-group-line', title: 'パートナー精神', desc: 'お客様を長期パートナーとして捉え、製造を超えたコンサルティングサポートを提供します。' },
];

const stats = [
  { value: '3,200+', label: '代工品目数', icon: 'ri-capsule-line' },
  { value: '6', label: 'コア認証数', icon: 'ri-award-line' },
  { value: '30+', label: '選択可能な剤型', icon: 'ri-test-tube-line' },
  { value: '4', label: '生産拠点', icon: 'ri-building-line' },
];

const expertiseBlocks = [
  {
    tag: 'R&D能力',
    title: '自社R&D、処方イノベーション',
    desc: '康永生技は独自のR&D研究室を保有し、食品科学・栄養学・生化学の専門研究者が在籍。国際的な原料トレンドを常に追い、お客様のために差別化された独自処方を開発します。',
    points: [
      '食品科学・生技分野の修士・博士研究員が多数在籍',
      '自社分析機器を保有、処方安定性テストに対応',
      '国際原料展への定期参加でトレンドを先取り',
      '必要に応じてNDA（秘密保持契約）に対応',
    ],
    img: 'https://readdy.ai/api/search-image?query=Asian%20female%20scientist%20in%20white%20lab%20coat%20carefully%20pipetting%20herbal%20extract%20liquid%20into%20test%20tubes%20in%20a%20bright%20modern%20biotechnology%20research%20laboratory%2C%20rows%20of%20green%20plant%20samples%20on%20workbench%2C%20warm%20natural%20daylight%20streaming%20through%20window%2C%20shallow%20depth%20of%20field&width=720&height=500&seq=expertise-rd-02&orientation=landscape',
    imgLeft: false,
  },
  {
    tag: '製造',
    title: 'GMP工場、厳格な工程管理',
    desc: '康永生技の工場はGMP認証を取得し、製造環境は医薬品グレードの基準を満たしています。原料受入から完成品出荷まで、すべての工程で完全な品質管理プロセスを実施し、安全性とバッチ一貫性を確保します。',
    points: [
      'GMP基準準拠のクリーンルーム生産環境',
      '全工程で温湿度を常時モニタリング',
      '多種類の剤型ライン：カプセル・錠剤・粉末・飲料',
      '全生産ロットに完全なバッチ検査報告書を発行',
    ],
    img: 'https://readdy.ai/api/search-image?query=modern%20GMP%20certified%20pharmaceutical%20manufacturing%20facility%20production%20line%20with%20stainless%20steel%20equipment%20workers%20in%20white%20uniforms%20hairnets%2C%20clean%20room%20environment%2C%20health%20supplement%20factory%20interior&width=720&height=500&seq=expertise-mfg-01&orientation=landscape',
    imgLeft: true,
  },
  {
    tag: '品質管理',
    title: '全工程品質管理、安全な市場投入',
    desc: '原料受入検査・工程内管理・完成品検査を網羅する包括的な品質管理システムを確立。すべての工程で標準作業手順書に基づき、台湾および輸出市場の法規要件への適合を確保します。',
    points: [
      '原料サプライヤー審査と定期監査制度',
      '完成品検査：微生物・重金属・安全指標',
      '健康食品認定申請・表示審査のサポート',
      '完全なバッチトレーサビリティシステム、製品追跡に対応',
    ],
    img: 'https://readdy.ai/api/search-image?query=quality%20control%20inspector%20testing%20health%20supplement%20products%20in%20laboratory%20holding%20clipboard%20checking%20bottles%20tablets%20capsules%2C%20professional%20QC%20process%20in%20biotech%20facility%2C%20clean%20bright%20environment&width=720&height=500&seq=expertise-qc-01&orientation=landscape',
    imgLeft: false,
  },
];

const facilities = [
  {
    title: 'R&D研究室',
    desc: '最新分析機器を完備し、処方開発・安定性試験に対応',
    img: 'https://readdy.ai/api/search-image?query=modern biotech research laboratory interior with analytical instruments microscopes equipment white clean room, scientist working, professional R&D facility for health supplement development&width=600&height=420&seq=facility-lab-01&orientation=landscape',
  },
  {
    title: 'クリーンルーム生産棟',
    desc: 'Class 10,000クリーンルーム、GMP基準の生産環境',
    img: 'https://readdy.ai/api/search-image?query=pharmaceutical%20clean%20room%20production%20facility%20workers%20in%20white%20protective%20suits%20hairnets%20working%20on%20assembly%20line%2C%20GMP%20certified%20manufacturing%20environment%2C%20stainless%20steel%20equipment&width=600&height=420&seq=facility-clean-01&orientation=landscape',
  },
  {
    title: 'QC検査室',
    desc: '充実した検査設備、全ロットに合格検査報告書を発行',
    img: 'https://readdy.ai/api/search-image?query=quality%20control%20laboratory%20technician%20testing%20supplement%20product%20samples%20with%20professional%20instruments%2C%20clean%20bright%20lab%20environment%2C%20health%20food%20safety%20testing%20facility&width=600&height=420&seq=facility-qc-01&orientation=landscape',
  },
  {
    title: '倉庫・物流エリア',
    desc: '温度管理された倉庫管理で原料・完成品を安全に保管',
    img: 'https://readdy.ai/api/search-image?query=modern%20pharmaceutical%20warehouse%20storage%20facility%20with%20organized%20shelves%20products%20boxes%20temperature%20controlled%20environment%2C%20professional%20logistics%20management%2C%20clean%20orderly%20storage&width=600&height=420&seq=facility-warehouse-01&orientation=landscape',
  },
];

const cases = [
  {
    category: 'カプセル剤型',
    title: '複合植物エキスカプセルシリーズ',
    desc: '複数の天然植物エキスを組み合わせたカスタム処方。健康維持訴求に適し、EC事業者を中心に高い支持を得ています。',
    img: 'https://readdy.ai/api/search-image?query=natural%20herb%20botanical%20capsule%20supplement%20product%20with%20green%20plants%20ingredients%20on%20clean%20white%20marble%20background%2C%20professional%20health%20product%20photography%2C%20elegant%20minimal%20packaging&width=600&height=400&seq=case-capsule-01&orientation=landscape',
    tags: ['カプセル', '植物エキス', 'カスタム処方'],
  },
  {
    category: 'ペット健康',
    title: 'ペット機能性健康シリーズ',
    desc: 'ペット市場向けに開発され、飼料添加物関連法規に適合したペット専用健康処方を提供します。',
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20product%20cat%20dog%20natural%20organic%20treats%20vitamins%20on%20light%20background%2C%20cute%20packaging%20pet%20care%20product%20photography%2C%20clean%20minimal%20style&width=600&height=400&seq=case-pet-01&orientation=landscape',
    tags: ['ペット健康', '機能性処方', '法規適合'],
  },
  {
    category: '飲料剤型',
    title: '機能性飲料OEMシリーズ',
    desc: '液体剤型の成熟した製造技術でコラーゲンドリンクから機能性飲料まで、市場のヒット商品づくりをサポートします。',
    img: 'https://readdy.ai/api/search-image?query=functional%20health%20drink%20bottle%20supplement%20beverage%20product%20display%20on%20clean%20light%20background%2C%20collagen%20protein%20drink%20packaging%2C%20professional%20product%20photography%20with%20natural%20green%20elements&width=600&height=400&seq=case-drink-01&orientation=landscape',
    tags: ['飲料', 'コラーゲン', '機能性ドリンク'],
  },
];

const reasons = [
  { icon: 'ri-flask-line', title: '自社R&D研究室', desc: '原料選定から処方設計まで、専門研究者チームが全面的な技術サポートを提供します。' },
  { icon: 'ri-verified-badge-line', title: 'GMP認定工場', desc: '国際GMP基準に準拠した安全・安定・一貫した製造環境を整備しています。' },
  { icon: 'ri-global-line', title: '多国輸出経験', desc: '各国の法規に精通し、海外の健康食品市場への展開を円滑にサポートします。' },
  { icon: 'ri-time-line', title: '短納期対応', desc: '綿密な生産スケジュール管理により、サンプルから量産までのリードタイムを短縮します。' },
  { icon: 'ri-customer-service-2-line', title: '専任アカウントサービス', desc: '担当営業が相談から出荷まで一貫して伴走し、丁寧にサポートします。' },
  { icon: 'ri-leaf-line', title: '天然原料優先', desc: '優良な天然原料を厳選し、原料調達を管理することで市場トレンドに合致した製品を実現します。' },
];

const subPages = [
  { title: 'コア強み', desc: 'GMP認定、R&D専門知識、充実した法規対応など、康永を際立たせる強みをご紹介します。', href: '/ja/about/core-advantages', icon: 'ri-medal-line' },
  { title: '専門生産', desc: 'GMP認定工場と品質管理システムについてご紹介します。', href: '/ja/about/production', icon: 'ri-building-2-line' },
  { title: '成功事例', desc: 'コンセプトから市場投入まで、実際のブランド支援実績をご紹介します。', href: '/ja/about/cases', icon: 'ri-trophy-line' },
  { title: '法規リスク管理', desc: '健康食品の表示コンプライアンスと法規リスクマネジメントについてご説明します。', href: '/ja/about/regulation', icon: 'ri-file-shield-2-line' },
];

export default function JaAboutPage() {
  const [_open] = useState(false);

  useSEO({
    title: '康永生技について｜GMP認定 OEM/ODMメーカー｜台湾',
    description: '康永生技は台湾のGMP認定健康食品OEM/ODMメーカー。自社R&D研究室、品質管理、多国輸出実績を持ち、600以上のブランドから信頼されています。',
    keywords: '康永生技, GMP認定工場, 健康食品OEM会社, 台湾生技メーカー',
    lang: 'ja',
    canonical: 'https://kangyong.com/ja/about',
  });

  return (
    <JaLayout>
      {/* Hero */}
      <section className="relative pt-20 min-h-[420px] md:min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20biotech%20pharmaceutical%20factory%20interior%20with%20clean%20production%20line%20equipment%20stainless%20steel%20tanks%20workers%20in%20white%20uniforms%2C%20bright%20professional%20industrial%20environment%2C%20health%20supplement%20manufacturing%20facility&width=1920&height=480&seq=about-hero-bg-01&orientation=landscape"
            alt="康永生技について"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/85 via-[#1a3a1a]/60 to-[#1a3a1a]/30" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-16 pt-24">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 tracking-wide">
              単なる製造業者を超えて
            </span>
            <h1 className="text-white font-bold leading-tight mb-5">
              <span className="block">あなたの信頼できる</span>
              <span className="block text-green-300">生技イノベーションパートナー</span>
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl mb-8">
              康永生技は専門的なR&D能力と厳格な品質管理体制でブランドのコンセプトから市場投入まで伴走し、健康食品製造において最も信頼できる長期パートナーとして機能します。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ja/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                相談する
              </Link>
              <Link to="/ja/services" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                サービスを見る
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              康永について
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              科学を基盤に、品質へのコミットメント
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技は台湾に拠点を置く健康食品のODM/OEM専門メーカー。R&D力と製造経験を組み合わせ、ブランドの成長を支える最も頼れるパートナーです。
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16 mb-20">
            <div className="w-full lg:w-1/2">
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                創業以来、康永生技は「専門性・品質・信頼」を核心価値として、健康食品ブランドに最も包括的なOEM/ODMサービスを提供することに注力してきました。処方R&D・原料調達・生産・品質管理・包装出荷まで一貫して対応するため、お客様はブランド経営に専念いただけます。
              </p>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                これまでスタートアップブランド、調剤薬局チェーン、EC事業者、国際輸出商など多様なクライアントにサービスを提供し、豊富な製造経験と市場インサイトを蓄積。健康食品市場への参入に最適なパートナーです。
              </p>
              <ul className="space-y-3">
                {introPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.9375rem] leading-[1.75]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=team%20of%20professional%20biotech%20scientists%20researchers%20discussing%20in%20modern%20laboratory%20with%20plants%20herbs%20ingredients%20on%20table%2C%20collaborative%20meeting%20environment%2C%20natural%20lighting%2C%20health%20supplement%20company%20team&width=720&height=500&seq=about-company-01&orientation=landscape"
                  alt="康永生技チーム"
                  className="w-full h-[360px] lg:h-[420px] object-cover object-top rounded-2xl"
                />
                <div className="absolute bottom-5 right-5 bg-white/95 rounded-xl px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
                    <i className="ri-building-2-line text-[#4CAF50] text-lg" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">長年のOEM実績</p>
                    <p className="text-gray-400 text-[0.75rem]">台湾生技業界に根ざした実力</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandValues.map((v) => (
              <div key={v.title} className="bg-[#f7faf7] rounded-2xl p-6 text-center hover:border-green-200 border border-transparent hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-xl mx-auto mb-4 border border-green-100">
                  <i className={`${v.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{v.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              専門背景
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              R&amp;D × 製造 × 品質管理<br className="hidden sm:block" />三位一体の専門力
            </h2>
          </div>
          <div className="space-y-16">
            {expertiseBlocks.map((block) => (
              <div
                key={block.title}
                className={`flex flex-col ${block.imgLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}
              >
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.8125rem] font-medium px-3 py-1 rounded-full mb-4">
                    {block.tag}
                  </span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-4">{block.title}</h3>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{block.desc}</p>
                  <ul className="space-y-3">
                    {block.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                        </div>
                        <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full lg:w-1/2 flex-shrink-0">
                  <div className="rounded-2xl overflow-hidden">
                    <img src={block.img} alt={block.title} className="w-full h-[320px] lg:h-[380px] object-cover object-top rounded-2xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              数字で見る康永
            </span>
            <h2 className="text-[#1a2e1a] font-bold">数字が語る実力</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#f7faf7] rounded-2xl p-8 text-center border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4">
                  <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                </div>
                <p className="text-[#1a2e1a] font-bold text-[2.125rem] leading-none mb-2">{s.value}</p>
                <p className="text-gray-500 text-[0.9375rem]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GMP Facility */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              施設紹介
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              GMP基準工場、安心の一貫生産
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技の工場は複数の国際認証を取得。R&Dから出荷まで最高水準の生産環境を提供しています。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-full h-[240px] overflow-hidden">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-5">
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { num: '3,200+', label: '累計代工品目数' },
              { num: '6', label: '国際認証取得数' },
              { num: '30+', label: '選択可能な剤型' },
              { num: '4', label: '生産拠点数' },
            ].map((item) => (
              <div key={item.label} className="bg-[#4CAF50] rounded-xl py-6 text-center">
                <p className="text-white font-bold text-[2rem] leading-none mb-1">{item.num}</p>
                <p className="text-green-100 text-[0.875rem]">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              製造事例
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">多様な製品、豊富な実績</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技は幅広い製品カテゴリに対応。以下は代表的な製造事例の一部です。
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cases.map((c) => (
              <div key={c.title} className="bg-[#f7faf7] rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300 group cursor-default">
                <div className="w-full h-[220px] overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.75rem] font-medium px-3 py-1 rounded-full mb-3">{c.category}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{c.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{c.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {c.tags.map((tag) => (
                      <span key={tag} className="bg-white border border-gray-200 text-gray-500 text-[0.75rem] px-3 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/ja/services" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
              サービスをもっと見る <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            <div className="w-full lg:w-2/5 flex-shrink-0">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                なぜ康永を選ぶのか
              </span>
              <h2 className="text-[#1a2e1a] font-bold leading-tight mb-6">
                600以上のブランドが<br />選ぶ理由とは？
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">
                私たちは単なる製造業者ではなく、ブランド成功の戦略的パートナーです。製品開発から市場投入まで、康永が一貫したプロフェッショナルサポートを提供します。
              </p>
              <Link
                to="/ja/contact/form"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-4 rounded-full transition-all duration-300 cursor-pointer"
              >
                今すぐお問い合わせ <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="w-full lg:w-3/5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reasons.map((r) => (
                  <div key={r.title} className="bg-white rounded-xl p-5 flex gap-4 border border-gray-100 hover:border-green-200 transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0">
                      <i className={`${r.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <div>
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{r.title}</h3>
                      <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-page nav */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">詳しく見る</h2>
            <p className="text-gray-500 text-[1.0625rem] max-w-2xl mx-auto">
              コア強み、製造能力、成功事例、法規対応の詳細をご覧ください。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {subPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="flex items-start gap-5 bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 flex-shrink-0 group-hover:bg-[#4CAF50] transition-colors duration-300">
                  <i className={`${page.icon} text-[#4CAF50] text-xl group-hover:text-white transition-colors duration-300`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{page.title}</h3>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.7]">{page.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[#4CAF50] text-[0.875rem] font-medium mt-3">
                    詳しく見る <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=vibrant%20lush%20green%20tropical%20botanical%20garden%20with%20sunlight%20filtering%20through%20leaves%2C%20rich%20emerald%20green%20foliage%20background%2C%20health%20nature%20wellness%20theme%2C%20beautiful%20natural%20scene&width=1920&height=600&seq=about-cta-bg-01&orientation=landscape"
            alt="CTA背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1a3a1a]/82" />
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-6">
            提携を始める
          </span>
          <h2 className="text-white font-bold leading-tight mb-6">
            康永生技を製造パートナーに<br />する準備はできていますか？
          </h2>
          <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-10">
            スタートアップブランドでも成熟した企業でも、康永生技はお客様のニーズに最適なOEM/ODMソリューションをご提案します。ぜひ営業チームにお問い合わせください。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ja/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              相談する
            </Link>
            <Link to="/ja/partnership" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              提携フローを見る
            </Link>
          </div>
        </div>
      </section>
    </JaLayout>
  );
}
